/**
 * Pagos (DEMO) — selector de pasarela + transacciones.
 *
 * 100% mock. Estado vive en useState durante la sesión.
 *
 *   1. Cards con 4 pasarelas: Wompi, Bold, ePayco, Stripe.
 *   2. Click en una → se expande con los inputs de API key / secret / mode.
 *   3. Solo una puede estar "activa" a la vez. Las demás quedan disponibles.
 *   4. Tabla de transacciones abajo, leída del mock store.
 */
import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, Input, Label, Button, Badge, PageHeader } from '@/components/ui'
import { tenantApi } from '@/lib/api'
import { CreditCard, ShieldCheck, Check, ChevronDown, ChevronUp, Eye, EyeOff, Lock, Sparkles } from 'lucide-react'

export const Route = createFileRoute('/store/$id/payments')({
  component: StorePaymentsPage,
})

type ProviderId = 'wompi' | 'bold' | 'epayco' | 'stripe'

interface ProviderField {
  key: string
  label: string
  placeholder: string
  type: 'text' | 'password' | 'select'
  options?: { value: string; label: string }[]
  helper?: string
}

interface ProviderInfo {
  id: ProviderId
  name: string
  countries: string[]
  description: string
  methods: string[]
  brandColor: string
  logoEmoji: string
  fields: ProviderField[]
  docsUrl: string
}

const PROVIDERS: ProviderInfo[] = [
  {
    id: 'wompi', name: 'Wompi', countries: ['CO'],
    description: 'Pasarela de Bancolombia. Tarjeta, PSE, Nequi y Botón Bancolombia.',
    methods: ['Tarjeta', 'PSE', 'Nequi', 'Bancolombia'],
    brandColor: 'from-cyan-500/20 to-cyan-500/5 border-cyan-500/40',
    logoEmoji: '🟢',
    docsUrl: 'https://docs.wompi.co',
    fields: [
      { key: 'publicKey',   label: 'Public Key',   placeholder: 'pub_test_xxxxxx', type: 'text' },
      { key: 'privateKey',  label: 'Private Key',  placeholder: 'prv_test_xxxxxx', type: 'password' },
      { key: 'eventsKey',   label: 'Events Key',   placeholder: 'evt_test_xxxxxx', type: 'password', helper: 'Para validar firmas de webhooks.' },
      { key: 'mode',        label: 'Modo',         placeholder: '', type: 'select', options: [
        { value: 'test', label: 'Pruebas (sandbox)' }, { value: 'live', label: 'Producción' },
      ]},
    ],
  },
  {
    id: 'bold', name: 'Bold', countries: ['CO'],
    description: 'Payment Link y checkout hosted. Tarjeta, PSE, Nequi y Botón Bancolombia.',
    methods: ['Tarjeta', 'PSE', 'Nequi', 'Bancolombia'],
    brandColor: 'from-violet-500/20 to-violet-500/5 border-violet-500/40',
    logoEmoji: '🟣',
    docsUrl: 'https://developers.bold.co',
    fields: [
      { key: 'apiKey',     label: 'API Key',     placeholder: 'bold_xxxxxxxxxxxxxx',  type: 'text' },
      { key: 'secretKey',  label: 'Secret Key',  placeholder: 'bold_secret_xxxxxxxx', type: 'password' },
      { key: 'mode',       label: 'Modo',        placeholder: '', type: 'select', options: [
        { value: 'test', label: 'Pruebas (sandbox)' }, { value: 'live', label: 'Producción' },
      ]},
    ],
  },
  {
    id: 'epayco', name: 'ePayco', countries: ['CO'],
    description: 'Onpage Checkout y Standard Checkout. Tarjeta, PSE, Daviplata y efectivo.',
    methods: ['Tarjeta', 'PSE', 'Daviplata', 'Efectivo'],
    brandColor: 'from-orange-500/20 to-orange-500/5 border-[hsl(var(--primary)/0.4)]',
    logoEmoji: '🟠',
    docsUrl: 'https://docs.epayco.com',
    fields: [
      { key: 'publicKey',   label: 'Public Key',   placeholder: '32 chars hex', type: 'text' },
      { key: 'privateKey',  label: 'Private Key',  placeholder: '32 chars hex', type: 'password' },
      { key: 'pCustId',     label: 'P_CUST_ID',    placeholder: 'ej: 1579240',  type: 'text' },
      { key: 'pKey',        label: 'P_KEY',        placeholder: '40 chars hex', type: 'password' },
      { key: 'mode',        label: 'Modo',         placeholder: '', type: 'select', options: [
        { value: 'test', label: 'Pruebas (sandbox)' }, { value: 'live', label: 'Producción' },
      ]},
    ],
  },
  {
    id: 'stripe', name: 'Stripe', countries: ['MX', 'US', 'EU', 'GLOBAL'],
    description: 'Pagos internacionales. Tarjeta, Apple Pay, Google Pay, Klarna y más.',
    methods: ['Tarjeta', 'Apple Pay', 'Google Pay', 'Klarna'],
    brandColor: 'from-indigo-500/20 to-indigo-500/5 border-indigo-500/40',
    logoEmoji: '🔵',
    docsUrl: 'https://stripe.com/docs',
    fields: [
      { key: 'publishableKey', label: 'Publishable Key', placeholder: 'pk_test_...', type: 'text' },
      { key: 'secretKey',      label: 'Secret Key',      placeholder: 'sk_test_...', type: 'password' },
      { key: 'webhookSecret',  label: 'Webhook Secret',  placeholder: 'whsec_...',   type: 'password', helper: 'Lo obtenés al crear un endpoint en Stripe.' },
      { key: 'mode',           label: 'Modo',            placeholder: '', type: 'select', options: [
        { value: 'test', label: 'Pruebas (sandbox)' }, { value: 'live', label: 'Producción' },
      ]},
    ],
  },
]

interface ConfigState {
  values: Record<string, string>
  connected: boolean
  connectedAt: string | null
}

interface Transaction {
  id: string
  orderId: string
  orderNumber: string
  amount: string
  currency: string
  provider: string
  method: string
  status: 'completed' | 'pending' | 'failed' | 'refunded'
  providerTransactionId: string
  createdAt: string
}

function StorePaymentsPage() {
  const [tab, setTab] = useState<'gateways' | 'transactions'>('gateways')
  const [configs, setConfigs] = useState<Record<ProviderId, ConfigState>>(() =>
    Object.fromEntries(
      PROVIDERS.map((p) => {
        const isPreSet = p.id === 'epayco'
        return [
          p.id,
          {
            values: isPreSet
              ? { publicKey: '2802a738***', privateKey: '••••••••••••', pCustId: '1579240', pKey: '••••••••••••', mode: 'test' }
              : {},
            connected: isPreSet,
            connectedAt: isPreSet ? '2026-05-08T14:00:00.000Z' : null,
          },
        ]
      }),
    ) as Record<ProviderId, ConfigState>,
  )

  const txQuery = useQuery({
    queryKey: ['payments-transactions'],
    queryFn: () => tenantApi.get<{ data: Transaction[] }>('/api/v1/transactions'),
  })

  const transactions = txQuery.data?.data ?? []
  const activeProvider = useMemo(
    () => PROVIDERS.find((p) => configs[p.id].connected) ?? null,
    [configs],
  )

  const setProviderConnected = (id: ProviderId, values: Record<string, string>) => {
    setConfigs((prev) => Object.fromEntries(
      Object.entries(prev).map(([k, v]) => [
        k,
        k === id
          ? { values, connected: true, connectedAt: new Date().toISOString() }
          : { ...v, connected: false },
      ]),
    ) as Record<ProviderId, ConfigState>)
  }

  const setProviderDisconnected = (id: ProviderId) => {
    setConfigs((prev) => ({ ...prev, [id]: { values: {}, connected: false, connectedAt: null } }))
  }

  return (
    <>
      <PageHeader
        title="Pagos"
        description="Selecciona tu pasarela y configurala en un par de clicks"
        action={
          activeProvider
            ? <Badge variant="success">Activa: {activeProvider.name}</Badge>
            : <Badge variant="warning">Sin pasarela activa</Badge>
        }
      />
      <div className="p-8 space-y-6">
        <div className="flex items-center gap-1 border-b border-border">
          <TabBtn active={tab === 'gateways'}     onClick={() => setTab('gateways')}><CreditCard className="size-3.5" /> Pasarelas</TabBtn>
          <TabBtn active={tab === 'transactions'} onClick={() => setTab('transactions')}><ShieldCheck className="size-3.5" /> Transacciones ({transactions.length})</TabBtn>
        </div>

        {tab === 'gateways' && (
          <>
            <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 p-4 flex items-start gap-3">
              <Sparkles className="size-5 text-blue-700 dark:text-blue-400 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-blue-700 dark:text-blue-200 font-medium">Conectá tu pasarela en menos de 2 minutos</p>
                <p className="text-blue-700 dark:text-blue-300/80 mt-1">
                  Solo una pasarela puede estar activa por tienda. Cuando conectes una nueva,
                  la anterior queda desactivada (las credenciales se preservan).
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {PROVIDERS.map((p) => (
                <ProviderCard
                  key={p.id}
                  provider={p}
                  state={configs[p.id]}
                  onConnect={(v) => setProviderConnected(p.id, v)}
                  onDisconnect={() => setProviderDisconnected(p.id)}
                />
              ))}
            </div>
          </>
        )}

        {tab === 'transactions' && (
          <TransactionsTable transactions={transactions} loading={txQuery.isLoading} />
        )}
      </div>
    </>
  )
}

function ProviderCard({ provider, state, onConnect, onDisconnect }: {
  provider: ProviderInfo
  state: ConfigState
  onConnect: (values: Record<string, string>) => void
  onDisconnect: () => void
}) {
  const [expanded, setExpanded] = useState(false)
  const [draft, setDraft] = useState<Record<string, string>>(state.values)
  const [revealed, setRevealed] = useState<Record<string, boolean>>({})
  const isComplete = provider.fields.every((f) => (draft[f.key] ?? '').trim().length > 0)

  return (
    <Card className={`p-0 overflow-hidden bg-gradient-to-br ${provider.brandColor}`}>
      <div className="p-5">
        <div className="flex items-start gap-4">
          <div className="text-3xl shrink-0">{provider.logoEmoji}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-base text-foreground">{provider.name}</h3>
              {state.connected && (
                <Badge variant="success" className="inline-flex items-center gap-1">
                  <Check className="size-3" /> Conectada
                </Badge>
              )}
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{provider.countries.join(' · ')}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{provider.description}</p>
            <div className="flex flex-wrap gap-1 mt-3">
              {provider.methods.map((m) => (
                <span key={m} className="text-[10px] px-2 py-0.5 rounded bg-[hsl(var(--surface))] text-foreground border border-border">
                  {m}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
          <a href={provider.docsUrl} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <Lock className="size-3" /> Documentación
          </a>
          <Button size="sm" variant={state.connected ? 'outline' : 'default'} onClick={() => setExpanded((v) => !v)}>
            {state.connected ? 'Editar credenciales' : 'Configurar'}
            {expanded ? <ChevronUp className="size-3.5 ml-1" /> : <ChevronDown className="size-3.5 ml-1" />}
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border bg-background/60 p-5 space-y-3">
          {provider.fields.map((field) => (
            <div key={field.key}>
              <Label htmlFor={`${provider.id}-${field.key}`} className="flex items-center justify-between">
                {field.label}
                {field.helper && <span className="text-[10px] text-muted-foreground font-normal normal-case">{field.helper}</span>}
              </Label>
              {field.type === 'select' ? (
                <select
                  id={`${provider.id}-${field.key}`}
                  value={draft[field.key] ?? ''}
                  onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })}
                  className="w-full bg-[hsl(var(--surface))] border border-border rounded px-3 py-2 text-sm text-foreground"
                >
                  <option value="">— Seleccionar —</option>
                  {field.options?.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              ) : (
                <div className="relative">
                  <Input
                    id={`${provider.id}-${field.key}`}
                    type={field.type === 'password' && !revealed[field.key] ? 'password' : 'text'}
                    placeholder={field.placeholder}
                    value={draft[field.key] ?? ''}
                    onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })}
                  />
                  {field.type === 'password' && (
                    <button
                      type="button"
                      onClick={() => setRevealed((r) => ({ ...r, [field.key]: !r[field.key] }))}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {revealed[field.key] ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
          <div className="flex items-center justify-between gap-2 pt-2">
            {state.connected ? (
              <Button variant="ghost" size="sm" onClick={() => { if (confirm(`¿Desconectar ${provider.name}?`)) onDisconnect() }} className="text-red-700 dark:text-red-400 hover:text-red-300">
                Desconectar
              </Button>
            ) : <span />}
            <Button size="sm" disabled={!isComplete} onClick={() => { onConnect(draft); setExpanded(false) }}>
              {state.connected ? 'Guardar cambios' : `Conectar ${provider.name}`}
            </Button>
          </div>
        </div>
      )}
    </Card>
  )
}

function TransactionsTable({ transactions, loading }: { transactions: Transaction[]; loading: boolean }) {
  const fmt = (amt: string) => `$ ${Number(amt).toLocaleString('es-CO')}`
  const date = (s: string) => new Date(s).toLocaleString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  const variant = (s: Transaction['status']) =>
    s === 'completed' ? 'success' : s === 'pending' ? 'warning' : s === 'refunded' ? 'muted' : 'destructive'
  const label = (s: Transaction['status']) =>
    s === 'completed' ? 'Aprobada' : s === 'pending' ? 'Pendiente' : s === 'refunded' ? 'Reembolsada' : 'Rechazada'

  return (
    <Card>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-12 text-center text-sm text-muted-foreground">Cargando…</div>
        ) : transactions.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">Sin transacciones aún.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="px-4 py-3">Fecha</th>
                  <th className="px-4 py-3">Orden</th>
                  <th className="px-4 py-3">Pasarela</th>
                  <th className="px-4 py-3">Método</th>
                  <th className="px-4 py-3 text-right">Monto</th>
                  <th className="px-4 py-3 text-center">Estado</th>
                  <th className="px-4 py-3">Ref. pasarela</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t) => (
                  <tr key={t.id} className="border-b border-border hover:bg-[hsl(var(--surface))]">
                    <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{date(t.createdAt)}</td>
                    <td className="px-4 py-3"><code className="text-xs text-foreground">{t.orderNumber}</code></td>
                    <td className="px-4 py-3 capitalize text-foreground">{t.provider}</td>
                    <td className="px-4 py-3 capitalize text-muted-foreground text-xs">{t.method}</td>
                    <td className="px-4 py-3 text-right text-foreground">{fmt(t.amount)} {t.currency}</td>
                    <td className="px-4 py-3 text-center">
                      <Badge variant={variant(t.status)}>{label(t.status)}</Badge>
                    </td>
                    <td className="px-4 py-3"><code className="text-[11px] text-muted-foreground">{t.providerTransactionId}</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
        active ? 'border-[hsl(var(--primary))] text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {children}
    </button>
  )
}
